import { lazy, type ComponentType } from 'react';

const STORAGE_KEY = 'app:lazyChunkRetried';

/**
 * Wraps React.lazy to auto-reload the page once if a dynamic import fails.
 *
 * This handles the classic SPA deploy issue: when a new frontend is
 * deployed, chunk hashes change and users with a stale index.html loaded
 * in their browser will 404 when trying to fetch old chunk filenames.
 * Cloudflare Pages' SPA fallback returns index.html (text/html) for
 * missing assets, which the browser rejects with a MIME-type error.
 *
 * On first failure, we reload the page to pick up the new index.html.
 * We store a sessionStorage flag so we only reload ONCE per session to
 * avoid infinite reload loops if the chunk is genuinely broken.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await importer();
      // Success — clear any prior retry flag
      sessionStorage.removeItem(STORAGE_KEY);
      return mod;
    } catch (err) {
      const alreadyRetried = sessionStorage.getItem(STORAGE_KEY) === 'true';
      if (!alreadyRetried) {
        sessionStorage.setItem(STORAGE_KEY, 'true');
        window.location.reload();
        // Return a never-resolving promise so Suspense stays pending while reload happens
        return new Promise<{ default: T }>(() => {});
      }
      // Already retried this session — rethrow to let ErrorBoundary handle it
      throw err;
    }
  });
}
